import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Input, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LockOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { usersApi } from '../../services/api';
import { useApi, useAsyncOperation, useAuth } from '../../hooks';
import { PAGE_SIZE, USER_ROLES } from '../../constants';
import { User } from '../../types';
import { formatDate } from '../../utils';
import {Md5} from "ts-md5";

const { Title, Text } = Typography;
const { Option } = Select;

const roleLabels: Record<string, string> = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
  [USER_ROLES.ADMIN]: 'Admin',
  [USER_ROLES.EDITOR]: 'Editor',
};

const Users: React.FC = () => {
  const [filterForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE });
  const [filters, setFilters] = useState<{ role?: string; activated?: boolean | undefined }>({});
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const { user } = useAuth();
  const isSuperAdmin = useMemo(
    () => user?.roles?.includes(USER_ROLES.SUPER_ADMIN),
    [user?.roles]
  );

  const { loading: actionLoading, execute: executeAsync } = useAsyncOperation();

  const getUsers = useCallback(() => {
    const values = filterForm.getFieldsValue();
    return usersApi.getUsers({
      pageIndex: pagination.current - 1,
      pageSize: pagination.pageSize,
      username: values.username?.trim() || undefined,
      fullName: values.fullName?.trim() || undefined,
      roles: filters.role ? [filters.role] : undefined,
      activated: filters.activated,
    });
  }, [filterForm, filters.activated, filters.role, pagination.current, pagination.pageSize]);

  const {
    data: usersData,
    loading: usersLoading,
    refresh,
  } = useApi(getUsers, { immediate: true });

  useEffect(() => {
    refresh();
  }, [pagination.current, pagination.pageSize, filters.role, filters.activated, refresh]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    refresh();
  };

  const handleReset = () => {
    filterForm.resetFields();
    setFilters({});
    setPagination({ current: 1, pageSize: PAGE_SIZE });
    refresh();
  };

  const handleCreateUser = async () => {
    const values = await createForm.validateFields();
    const result = await executeAsync(
      () =>
        usersApi.createUser({
          userName: values.userName.trim(),
          password: Md5.hashStr(values.password),
          roles: values.roles,
          activated: values.activated ?? true,
        }),
      { successMessage: 'User created successfully' }
    );

    if (result) {
      setCreateModalVisible(false);
      createForm.resetFields();
      refresh();
    }
  };

  const handleActivationToggle = async (checked: boolean, record: User) => {
    if (!isSuperAdmin) return;
    setUpdatingUserId(record.id);
    const result = await executeAsync(
      () =>
        usersApi.updateUserActivation(record.id, {
          activated: checked,
        }),
      { successMessage: checked ? 'User activated' : 'User deactivated' }
    );
    setUpdatingUserId(null);
    if (result) {
      refresh();
    }
  };

  const handleOpenPasswordModal = (targetUser: User) => {
    setSelectedUser(targetUser);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handleChangePassword = async () => {
    if (!selectedUser) return;
    const values = await passwordForm.validateFields();
    const result = await executeAsync(
      () =>
        usersApi.changeUserPassword(selectedUser.id, {
          password: Md5.hashStr(values.password),
        }),
      { successMessage: 'Password changed successfully' }
    );

    if (result) {
      setPasswordModalVisible(false);
      setSelectedUser(null);
      passwordForm.resetFields();
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Username',
      dataIndex: 'userName',
      render: (_: any, record) => record.userName || record.username || '-',
    },
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      render: (text: string | undefined) => text || '-',
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      render: (roles: string[]) => (
        <Space wrap>
          {roles?.map((role) => (
            <Tag key={role} color={role === USER_ROLES.SUPER_ADMIN ? 'magenta' : 'blue'}>
              {roleLabels[role] || role}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'activated',
      render: (activated: boolean, record) => (
        <Space>
          <Switch
            checked={activated}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            disabled={!isSuperAdmin}
            loading={updatingUserId === record.id && actionLoading}
            onChange={(checked) => handleActivationToggle(checked, record)}
          />
        </Space>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      render: (_: any, record) =>
        isSuperAdmin ? (
          <Button
            type="link"
            icon={<LockOutlined />}
            onClick={() => handleOpenPasswordModal(record)}
          >
            Reset Password
          </Button>
        ) : (
          <Text type="secondary">No Permission</Text>
        ),
    },
  ];

  return (
    <div>
      {/* Header Section */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Users
          </Title>
        </div>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => refresh()}
            style={{
              borderRadius: '6px',
              height: '40px',
            }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setCreateModalVisible(true);
            }}
            disabled={!isSuperAdmin}
            size="large"
            style={{
              background: '#1890ff',
              borderRadius: '6px',
              height: '40px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Create
          </Button>
        </Space>
      </div>

      {/* Filter Card */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Form form={filterForm} layout="vertical">
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 16,
            marginBottom: 16,
          }}>
            <Form.Item name="username" label="Username" style={{ marginBottom: 0 }}>
              <Input allowClear placeholder="Enter username" />
            </Form.Item>
            <Form.Item name="fullName" label="Full Name" style={{ marginBottom: 0 }}>
              <Input allowClear placeholder="Enter full name" />
            </Form.Item>
            <Form.Item label="Role" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                placeholder="Select role"
                value={filters.role}
                onChange={(value) => {
                  setFilters((prev) => ({ ...prev, role: value || undefined }));
                }}
              >
                <Option value={USER_ROLES.SUPER_ADMIN}>Super Admin</Option>
                <Option value={USER_ROLES.ADMIN}>Admin</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Status" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                placeholder="Select status"
                value={
                  filters.activated === undefined
                    ? undefined
                    : filters.activated
                }
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    activated: value === undefined ? undefined : value,
                  }))
                }
              >
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleReset}
              style={{
                borderRadius: '6px',
                height: '36px',
                fontSize: '14px',
              }}
            >
              Reset
            </Button>
            <Button 
              type="primary" 
              onClick={handleSearch}
              style={{
                background: '#1890ff',
                borderRadius: '6px',
                height: '36px',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Search
            </Button>
          </div>
        </Form>
      </Card>

      {/* Table Card */}
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Table
          rowKey="id"
          columns={columns}
          loading={usersLoading}
          dataSource={usersData?.data || []}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: usersData?.totalRecord || 0,
            showSizeChanger: false,
            onChange: (page) => setPagination((prev) => ({ ...prev, current: page })),
          }}
          scroll={{ x: 1000 }}
          style={{
            fontSize: '14px',
          }}
        />
      </Card>

      <Modal
        title="Create User"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onOk={handleCreateUser}
        confirmLoading={actionLoading}
        okText="Save"
        cancelText="Cancel"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="userName"
            label="Username"
            rules={[
              { required: true, message: 'Please enter username' },
              { min: 3, message: 'At least 3 characters' },
            ]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Initial Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'At least 6 characters' },
            ]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item
            name="roles"
            label="Roles"
            rules={[{ required: true, message: 'Please select at least one role' }]}
          >
            <Select mode="multiple" placeholder="Select roles">
              <Option value={USER_ROLES.SUPER_ADMIN}>Super Admin</Option>
              <Option value={USER_ROLES.ADMIN}>Admin</Option>
              <Option value={USER_ROLES.EDITOR}>Editor</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="activated"
            label="Status"
            initialValue={true}
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Reset Password for ${selectedUser?.userName || selectedUser?.username || ''}`}
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        onOk={handleChangePassword}
        confirmLoading={actionLoading}
        okText="Confirm"
        cancelText="Cancel"
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical" preserve={false}>
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'At least 6 characters' },
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;


